/**
 * Bottom sheets and the toast:
 *   CollectionSheet  the save picker: choose / move a card's collection, or a
 *                    new one by name; or manage collections (create, rename,
 *                    delete — never the default "Saved")
 *   ConfirmSheet     a yes / no question (deleting a collection)
 *   InviteSheet      PRD "Invite": Email (required), First / Last name,
 *                    Organization, LinkedIn or alternate URL per recipient.
 *                    "Add invite" is enabled only while every entry has a valid
 *                    email (so at least one); an entry's delete icon shows only
 *                    once its email field has something in it.
 *   Toast            the confirmation at the top of the screen
 */
import React, { useEffect, useRef, useState } from 'react';
import { Icon } from './icons';
import type { Collection, InviteRecipient, Kind } from './api';
import { sendInvite } from './api';
import { fmt } from './ui';
import { ocTrack } from './track';
import { useCollectionStyles } from './collectionStyles';

const SheetFrame: React.FC<{ title: string; onClose: () => void; children: React.ReactNode; footer?: React.ReactNode; labelId: string }> =
  ({ title, onClose, children, footer, labelId }) => {
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => { ref.current?.focus(); }, []);
    return (
      <div className="oc-sheet-wrap" onClick={onClose}>
        <div className="oc-sheet" role="dialog" aria-modal="true" aria-labelledby={labelId} tabIndex={-1} ref={ref}
             onClick={e => e.stopPropagation()}>
          <div className="oc-sheet-head">
            <h2 id={labelId} className="oc-h" style={{ fontSize: 17 }}>{title}</h2>
            <button type="button" className="oc-close" aria-label="Close" onClick={onClose}><Icon name="close" size={15} /></button>
          </div>
          {children}
          {footer}
        </div>
      </div>
    );
  };

// ---------------------------------------------------------------- collections
const SHEET_TITLE: Record<Kind, string> = { problem: 'Save problem card', company: 'Save company card', solution: 'Save COSMOS card' };

/** With a card (`target`): the save picker — the member's collections (the
 *  card's current one ticked), "New collection" with a name field that creates
 *  it and saves there, and Remove when the card is already saved. Without a
 *  card: manage collections (create, rename, delete — never the default). */
export const CollectionSheet: React.FC<{
  target: { kind: Kind; id: string } | null; collections: Collection[]; currentId: number | null;
  onPick: (collectionId: number) => void; onCreate: (name: string) => Promise<string>;
  onRename: (id: number, name: string) => Promise<string>; onDelete: (id: number) => void; onClose: () => void;
  onRemove?: () => void;
}> = ({ target, collections, currentId, onPick, onCreate, onRename, onDelete, onClose, onRemove }) => {
  useCollectionStyles();
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    const error = await onCreate(name.trim());
    setBusy(false);
    setErr(error);
    if (!error) setName('');
  };
  const rename = async () => {
    if (editing == null) return;
    const n = editName.trim();
    if (!n) { setEditing(null); return; }
    const error = await onRename(editing, n);
    setErr(error);
    if (!error) setEditing(null);
  };
  const title = !target ? 'Manage collections' : currentId ? 'Move to a collection' : SHEET_TITLE[target.kind];
  return (
    <SheetFrame title={title} onClose={onClose} labelId="oc-colsheet-h"
      footer={<>
        {err && <div className="oc-error" role="alert" style={{ margin: '0 18px 10px' }}>{err}</div>}
        <form className="oc-sheet-foot" onSubmit={create}>
          <input className="oc-input" style={{ flex: '1 1 180px', padding: '11px 13px', fontSize: 14 }} value={name} maxLength={60}
                 onChange={e => setName(e.target.value)} placeholder="New collection" aria-label="New collection name" />
          <button type="submit" className="oc-btn oc-btn-sm" disabled={!name.trim() || busy}>
            {target ? (currentId ? 'Create & move' : 'Create & save') : 'Create'}
          </button>
          {target && currentId && onRemove && (
            <button type="button" className="occ-remove" style={{ marginLeft: 'auto' }} onClick={onRemove}>Remove from saved</button>
          )}
        </form>
      </>}>
      <div className="oc-sheet-body" role={target ? 'radiogroup' : undefined} aria-label={target ? 'Collections' : undefined}>
        {target ? collections.map(c => {
          const current = currentId === c.id;
          return (
            <button key={c.id} type="button" role="radio" aria-checked={current} className="occ-pick"
                    onClick={() => (current ? onClose() : onPick(c.id))}>
              <span className="oc-colicon"><Icon name="bookmark" size={20} /></span>
              <span style={{ minWidth: 0 }}>
                <span className="oc-colname" style={{ display: 'block' }}>{c.name}</span>
                <span className="oc-hsub">{fmt(c.count)} card{c.count === 1 ? '' : 's'}</span>
              </span>
              <span className="tick"><Icon name={current ? 'tickCircleFilled' : 'tickCircle'} size={20} /></span>
            </button>
          );
        }) : collections.map(c => (
          <div key={c.id} className="oc-colitem">
            <div className="oc-colicon"><Icon name="bookmark" size={20} /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              {editing === c.id ? (
                <div className="oc-row" style={{ gap: 8 }}>
                  <input className="oc-input" style={{ fontSize: 14, borderColor: 'var(--gray-400)' }} value={editName} maxLength={60} autoFocus
                         aria-label={`Rename ${c.name}`} onChange={e => setEditName(e.target.value)}
                         onKeyDown={e => { if (e.key === 'Enter') rename(); if (e.key === 'Escape') { e.stopPropagation(); setEditing(null); } }} />
                  <button type="button" className="oc-link" onClick={rename}>Done</button>
                </div>
              ) : (
                <button type="button" style={{ width: '100%', textAlign: 'left' }} onClick={() => onPick(c.id)} aria-label={`Show ${c.name}`}>
                  <div className="oc-colname">{c.name}</div>
                  <div className="oc-hsub">{fmt(c.count)} card{c.count === 1 ? '' : 's'}{c.is_default ? ' · default' : ''}</div>
                </button>
              )}
            </div>
            <div className="oc-row" style={{ gap: 4 }}>
              <button type="button" className="oc-ico" aria-label={`Rename ${c.name}`} title="Rename collection"
                      onClick={() => { setEditing(c.id); setEditName(c.name); setErr(''); }}><Icon name="edit" size={16} /></button>
              {!c.is_default && (
                <button type="button" className="oc-ico" aria-label={`Delete ${c.name}`} title="Delete collection — its cards move to the default collection"
                        onClick={() => onDelete(c.id)}><Icon name="trash" size={16} /></button>
              )}
            </div>
          </div>
        ))}
      </div>
    </SheetFrame>
  );
};

/** A yes / no question in a sheet (deleting a collection). */
export const ConfirmSheet: React.FC<{
  title: string; body: React.ReactNode; confirmLabel: string; danger?: boolean; onConfirm: () => void; onClose: () => void;
}> = ({ title, body, confirmLabel, danger, onConfirm, onClose }) => (
  <SheetFrame title={title} onClose={onClose} labelId="oc-confirm-h"
    footer={
      <div className="oc-sheet-foot" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="oc-btn oc-btn-sm oc-btn-ghost" onClick={onClose}>Cancel</button>
        <button type="button" className="oc-btn oc-btn-sm" onClick={onConfirm}
                style={danger ? { background: 'var(--error-300)', borderColor: 'var(--error-300)' } : undefined}>{confirmLabel}</button>
      </div>
    }>
    <div className="oc-sheet-body">
      <p className="oc-hsub" style={{ fontSize: 14, lineHeight: '21px', margin: '8px 8px 12px', color: 'var(--gray-400)' }}>{body}</p>
    </div>
  </SheetFrame>
);

// ---------------------------------------------------------------- invite
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const MAX_RECIPIENTS = 10;
const blank = (): InviteRecipient => ({ email: '', first_name: '', last_name: '', organisation: '', url: '' });
const validEmail = (s: string) => EMAIL_RE.test(s.trim());
const validUrl = (s: string) => {
  const v = s.trim();
  if (!v) return true;
  try { const u = new URL(/^https?:\/\//i.test(v) ? v : `https://${v}`); return u.hostname.includes('.'); } catch { return false; }
};

export const InviteSheet: React.FC<{ onClose: () => void; onDone: (msg: string) => void }> = ({ onClose, onDone }) => {
  const [rows, setRows] = useState<InviteRecipient[]>([blank()]);
  const [touched, setTouched] = useState<Record<number, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ sent: string[]; existing: string[]; failed: { email: string; error: string }[] } | null>(null);

  const set = (i: number, k: keyof InviteRecipient, v: string) => setRows(rs => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const filled = rows.filter(r => r.email.trim());
  const allValid = rows.length > 0 && rows.every(r => validEmail(r.email) && validUrl(r.url));
  const canAdd = allValid && rows.length < MAX_RECIPIENTS;
  const canSend = filled.length > 0 && filled.every(r => validEmail(r.email) && validUrl(r.url)) && !busy;
  const remove = (i: number) => {
    setRows(rs => (rs.length === 1 ? [blank()] : rs.filter((_, j) => j !== i)));
    setTouched({});
  };
  const send = async () => {
    if (!canSend) return;
    setBusy(true);
    const seen = new Set<string>();
    const out = { sent: [] as string[], existing: [] as string[], failed: [] as { email: string; error: string }[] };
    for (const r of filled) {
      const email = r.email.trim().toLowerCase();
      if (seen.has(email)) continue;
      seen.add(email);
      const res = await sendInvite(r);
      if (res.ok) out.sent.push(email);
      else if (res.exists) out.existing.push(email);
      else out.failed.push({ email, error: res.error });
    }
    setBusy(false);
    setResult(out);
    ocTrack('ocean_invite_send', { count: seen.size, sent: out.sent.length, existing: out.existing.length });
    if (!out.failed.length) {
      setRows([blank()]);
      setTouched({});
      if (out.sent.length) onDone(`${out.sent.length} invitation${out.sent.length === 1 ? '' : 's'} sent.`);
    }
  };

  return (
    <SheetFrame title="Invite to UniversityTech" onClose={onClose} labelId="oc-invite-h"
      footer={
        <div className="oc-sheet-foot" style={{ justifyContent: 'space-between' }}>
          <button type="button" className="oc-btn oc-btn-sm oc-btn-ghost" disabled={!canAdd}
                  title={canAdd ? 'Add another person' : 'Enter a valid email first'}
                  onClick={() => setRows(rs => [...rs, blank()])}>
            <Icon name="add" size={14} />Add invite
          </button>
          <button type="button" className="oc-btn oc-btn-sm" disabled={!canSend} onClick={send}>
            {busy ? 'Sending…' : `Send invite${filled.length > 1 ? 's' : ''}`}
          </button>
        </div>
      }>
      <div className="oc-sheet-body">
        <p className="oc-hsub" style={{ fontSize: 14, lineHeight: '21px', margin: '6px 8px 4px', color: 'var(--gray-400)' }}>
          Find us interesting and helpful? Invite your friends, colleagues, or team to join us!
        </p>
        {rows.map((r, i) => {
          const badEmail = !!touched[i] && !!r.email.trim() && !validEmail(r.email);
          const badUrl = !validUrl(r.url);
          return (
            <div key={i} className="oc-recipient">
              <div className="oc-wide oc-email-row">
                <label className="oc-field">
                  <span>Email *</span>
                  <input type="email" value={r.email} autoComplete="off" aria-invalid={badEmail} required
                         onChange={e => set(i, 'email', e.target.value)} onBlur={() => setTouched(t => ({ ...t, [i]: true }))}
                         placeholder="name@organisation.com" />
                  {badEmail && <em>Enter a valid email address.</em>}
                </label>
                {r.email.trim() && (
                  <button type="button" className="oc-ico" style={{ marginBottom: badEmail ? 22 : 4 }}
                          aria-label={`Remove ${r.email.trim()}`} title="Remove this person" onClick={() => remove(i)}>
                    <Icon name="trash" size={16} />
                  </button>
                )}
              </div>
              <label className="oc-field"><span>First name</span>
                <input value={r.first_name} maxLength={80} onChange={e => set(i, 'first_name', e.target.value)} /></label>
              <label className="oc-field"><span>Last name</span>
                <input value={r.last_name} maxLength={80} onChange={e => set(i, 'last_name', e.target.value)} /></label>
              <label className="oc-field oc-wide"><span>Organization</span>
                <input value={r.organisation} maxLength={160} onChange={e => set(i, 'organisation', e.target.value)} /></label>
              <label className="oc-field oc-wide"><span>LinkedIn or alternate URL</span>
                <input value={r.url} maxLength={300} aria-invalid={badUrl} onChange={e => set(i, 'url', e.target.value)}
                       placeholder="linkedin.com/in/…" />
                {badUrl && <em>Enter a web address, e.g. linkedin.com/in/name.</em>}
              </label>
            </div>
          );
        })}
        {result && (
          <div className="oc-hsub" role="status" style={{ margin: '4px 10px 10px', fontSize: 13, lineHeight: '20px' }}>
            {result.sent.length > 0 && <div>Sent to {result.sent.join(', ')}.</div>}
            {result.existing.length > 0 && <div>Already on UniversityTech: {result.existing.join(', ')}.</div>}
            {result.failed.map(f => <div key={f.email} style={{ color: 'var(--error-300)' }}>Not sent to {f.email}: {f.error}</div>)}
          </div>
        )}
      </div>
    </SheetFrame>
  );
};

// ---------------------------------------------------------------- toast
/** action / action2: up to two links ("Change" and "View in Profile" after a save). */
export interface ToastState { msg: string; action?: { label: string; run: () => void }; action2?: { label: string; run: () => void }; sticky?: boolean }

export const Toast: React.FC<{ t: ToastState | null; onDismiss: () => void }> = ({ t, onDismiss }) => {
  useEffect(() => {
    if (!t) return;
    const id = window.setTimeout(onDismiss, t.sticky ? 12000 : t.action2 ? 6000 : t.action ? 4500 : 2400);
    return () => window.clearTimeout(id);
  }, [t, onDismiss]);
  if (!t) return null;
  return (
    <div className="oc-toast" role="status" aria-live="polite">
      <span style={{ overflowWrap: 'anywhere', userSelect: 'all' }}>{t.msg}</span>
      {t.action && <button type="button" onClick={() => { t.action!.run(); onDismiss(); }}>{t.action.label}</button>}
      {t.action2 && <button type="button" onClick={() => { t.action2!.run(); onDismiss(); }}>{t.action2.label}</button>}
      {t.sticky && <button type="button" aria-label="Dismiss" onClick={onDismiss} style={{ textDecoration: 'none' }}>✕</button>}
    </div>
  );
};
